import { useCallback, useEffect, useState } from 'react';
import { useFileScan } from 'services/api/FileUpload';
import { useGTM } from 'hooks/useGTM';
import { useToastNotify } from '@opswat/ui';
import { useTranslation } from 'react-i18next';
import { ScanOptions } from 'services/interface';
import {
    ARCHIVE_PASSWORD_SESSION_KEY,
    SCAN_OPTIONS_STORAGE_KEY,
    SELECTED_SCAN_TYPE_STORAGE_KEY,
    SELECTED_WORKFLOW_STORAGE_KEY,
} from '../config/scanStorageKeys';
import { DEFAULT_WORKFLOW_IDS } from 'utils/workflows';
import { buildScanRules } from 'hooks/useFileScanFlow';
import { useUser } from 'services/User';

const DEFAULT_SCAN_OPTIONS: ScanOptions = {
    multiscanning: false,
    deepCdr: false,
    adaptiveSandbox: false,
    proactiveDlp: false,
    unarchiving: false,
    privateScan: false,
};

type UseGlobalDragDropOptions = {
    /** When false, the global drag listeners are not attached (e.g. non-homepage public routes). Defaults to true. */
    enabled?: boolean;
    /** Scan options used when none are persisted in localStorage (e.g. public landing guests). */
    fallbackOptions?: ScanOptions;
};

export const useGlobalDragDrop = ({
    enabled = true,
    fallbackOptions = DEFAULT_SCAN_OPTIONS,
}: UseGlobalDragDropOptions = {}) => {
    const { scan } = useFileScan();
    const { user } = useUser();
    const isGuest = !user?.isLoggedIn;
    const { trackClick } = useGTM();
    const { toastNotify } = useToastNotify();
    const { t } = useTranslation();
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [dragDepth, setDragDepth] = useState(0);

    interface StoredWorkflow {
        _id?: string;
        name?: string;
    }

    const getSelectedWorkflow = useCallback((): StoredWorkflow | null => {
        if (typeof window === 'undefined') return null;
        const saved = localStorage.getItem(SELECTED_WORKFLOW_STORAGE_KEY);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch {
                return null;
            }
        }
        return null;
    }, []);

    const getSelectedScanType = useCallback((): string => {
        if (typeof window === 'undefined') {
            return '';
        }
        return localStorage.getItem(SELECTED_SCAN_TYPE_STORAGE_KEY) ?? '';
    }, []);

    const handleFileSelect = useCallback(
        (files: FileList | null) => {
            if (!files || files.length === 0) {
                return;
            }
            const file = files[0];
            if (file.size === 0) {
                toastNotify({
                    title: t('Error'),
                    message: t('Cannot upload an empty file.'),
                    type: 'error',
                    hasClose: true,
                    duration: 4000,
                });
                return;
            }

            setIsUploading(true);
            trackClick('global-drag-drop-file-upload');
            let persistedOptions = fallbackOptions;
            const saved = typeof window !== 'undefined' ? localStorage.getItem(SCAN_OPTIONS_STORAGE_KEY) : null;
            if (saved) {
                const parsed = JSON.parse(saved);
                persistedOptions = { ...fallbackOptions, ...parsed };
            }

            const selectedWorkflow = getSelectedWorkflow();
            const selectedScanType = getSelectedScanType();
            const isWorkflowScanType = selectedScanType === 'workflow';
            // Custom Workflow with no workflow chosen must not fall back to Custom Scan rules.
            if (isWorkflowScanType && !selectedWorkflow) {
                setIsUploading(false);
                toastNotify({
                    title: t('Select a workflow'),
                    message: t('Select a custom workflow to perform a scan'),
                    type: 'neutral',
                    hasClose: true,
                    duration: 4000,
                });
                return;
            }
            const rules =
                isWorkflowScanType && selectedWorkflow
                    ? typeof selectedWorkflow._id === 'string' && DEFAULT_WORKFLOW_IDS.includes(selectedWorkflow._id)
                        ? selectedWorkflow._id.split('_').join(', ')
                        : selectedWorkflow.name || selectedWorkflow._id || ''
                    : buildScanRules(persistedOptions, isGuest);
            const adaptiveSandbox = isWorkflowScanType ? false : !!persistedOptions.adaptiveSandbox;
            const privateScan = !!persistedOptions.privateScan;
            const savedPasswords =
                typeof window !== 'undefined' ? sessionStorage.getItem(ARCHIVE_PASSWORD_SESSION_KEY) : null;
            const archivePasswords = (savedPasswords ? JSON.parse(savedPasswords) : []) as string[];

            const shouldSendArchivePasswords = Boolean(
                archivePasswords.length > 0 &&
                    (persistedOptions.unarchiving || isWorkflowScanType),
            );

            scan(
                file,
                rules,
                adaptiveSandbox,
                privateScan,
                archivePasswords,
                undefined,
                undefined,
                shouldSendArchivePasswords,
            );
        },
        [
            fallbackOptions,
            getSelectedScanType,
            getSelectedWorkflow,
            isGuest,
            scan,
            t,
            toastNotify,
            trackClick,
        ],
    );

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') {
            return undefined;
        }

        const hasFiles = (event: DragEvent): boolean => {
            const types = event.dataTransfer?.types;
            return Boolean(types && Array.from(types).includes('Files'));
        };

        const onDragEnter = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            setDragDepth((prev) => prev + 1);
            setIsDragOver(true);
        };

        const onDragOver = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            setIsDragOver(true);
        };

        const onDragLeave = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            setDragDepth((prev) => Math.max(0, prev - 1));
        };

        const onDrop = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            setDragDepth(0);
            setIsDragOver(false);
            handleFileSelect(event.dataTransfer?.files ?? null);
        };

        window.addEventListener('dragenter', onDragEnter);
        window.addEventListener('dragover', onDragOver);
        window.addEventListener('dragleave', onDragLeave);
        window.addEventListener('drop', onDrop);

        return () => {
            window.removeEventListener('dragenter', onDragEnter);
            window.removeEventListener('dragover', onDragOver);
            window.removeEventListener('dragleave', onDragLeave);
            window.removeEventListener('drop', onDrop);
        };
    }, [enabled, handleFileSelect]);

    useEffect(() => {
        if (dragDepth === 0) {
            setIsDragOver(false);
        }
    }, [dragDepth]);

    return {
        isDragOver,
        isUploading,
        handleFileSelect,
    };
};
